/** Bucket daily series labels (YYYY-MM-DD) into day/week/month buckets for chart display. */

export type Granularity = 'day' | 'week' | 'month';

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inclusive number of calendar days between start and end (local). */
function inclusiveDaySpan(start: Date, end: Date): number {
  const s = startOfLocalDay(start);
  const e = startOfLocalDay(end);
  return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1;
}

export function granularityFromSpan(spanDays: number): Granularity {
  if (spanDays <= 7) return 'day';
  if (spanDays <= 62) return 'week';
  return 'month';
}

/** Monday as start of week (ISO-style). */
function mondayOfWeek(d: Date): Date {
  const copy = startOfLocalDay(d);
  const dow = copy.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

function monthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function bucketKeyForDay(d: Date, g: Granularity): string {
  if (g === 'day') return toYmd(d);
  if (g === 'week') return toYmd(mondayOfWeek(d));
  return monthKey(d);
}

function orderedBucketKeys(rangeStart: Date, rangeEnd: Date, g: Granularity): string[] {
  const s = startOfLocalDay(rangeStart);
  const e = startOfLocalDay(rangeEnd);
  if (g === 'day') {
    const keys: string[] = [];
    for (let cur = new Date(s); cur.getTime() <= e.getTime(); cur = addDays(cur, 1)) {
      keys.push(toYmd(cur));
    }
    return keys;
  }
  if (g === 'week') {
    const keys: string[] = [];
    const seen = new Set<string>();
    for (let cur = new Date(s); cur.getTime() <= e.getTime(); cur = addDays(cur, 1)) {
      const k = toYmd(mondayOfWeek(cur));
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
    return keys;
  }
  const keys: string[] = [];
  const seen = new Set<string>();
  for (let cur = new Date(s); cur.getTime() <= e.getTime(); cur = addDays(cur, 1)) {
    const k = monthKey(cur);
    if (!seen.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  }
  return keys;
}

function parseYmdToLocal(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatBucketLabel(key: string, g: Granularity, locale: string): string {
  if (g === 'day') {
    const d = parseYmdToLocal(key);
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d);
  }
  if (g === 'week') {
    const d = parseYmdToLocal(key);
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(d);
  }
  const [ys, ms] = key.split('-');
  const d = new Date(Number(ys), Number(ms) - 1, 1);
  return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(d);
}

/**
 * Aggregate daily API points into week/month buckets when the selected range is wide,
 * matching visits/meetings “over time” chart behaviour.
 */
export function bucketizeDailySeriesForChart(
  rangeStart: Date,
  rangeEnd: Date,
  dailyLabels: string[],
  dailyValues: number[],
  locale: string,
): { xLabels: string[]; values: number[] } {
  const dailyMap = new Map<string, number>();
  dailyLabels.forEach((label, idx) => {
    const v = dailyValues[idx] ?? 0;
    dailyMap.set(label, (dailyMap.get(label) ?? 0) + v);
  });

  const span = inclusiveDaySpan(rangeStart, rangeEnd);
  const g = granularityFromSpan(span);
  const bucketKeys = orderedBucketKeys(rangeStart, rangeEnd, g);
  const counts = new Map<string, number>(bucketKeys.map((k) => [k, 0]));

  for (let cur = startOfLocalDay(rangeStart); cur.getTime() <= startOfLocalDay(rangeEnd).getTime(); cur = addDays(cur, 1)) {
    const ymd = toYmd(cur);
    const cnt = dailyMap.get(ymd) ?? 0;
    const bk = bucketKeyForDay(cur, g);
    counts.set(bk, (counts.get(bk) ?? 0) + cnt);
  }

  const values = bucketKeys.map((k) => counts.get(k) ?? 0);
  const xLabels = bucketKeys.map((k) => formatBucketLabel(k, g, locale));
  return { xLabels, values };
}
